import { createChart, ColorType, LineStyle } from 'lightweight-charts'
import type {
  IChartApi,
  ISeriesApi,
  UTCTimestamp,
  DeepPartial,
  LineSeriesOptions,
  HistogramSeriesOptions,
} from 'lightweight-charts'
import { useEffect, useRef } from 'react'

import type { OhlcvBar } from '@/hooks/useCandles'
import { getIndicator } from '@/indicators/registry'
import type { ActiveIndicator } from '@/types/chart'
import type { IndicatorResult } from '@/types/indicator'

const HEADER_H = 24 // px — label bar above the chart

/** Default chart-area height (excluding header) per indicator type. */
const DEFAULT_CHART_H: Record<string, number> = {
  rsi: 100,
  macd: 130,
  atr: 80,
  obv: 80,
  mfi: 100,
  cmf: 80,
}

/** Default total pane height (chart + header) used when no height prop is supplied. */
export const PANE_H = (id: string) => (DEFAULT_CHART_H[id] ?? 100) + HEADER_H

interface Props {
  bars: OhlcvBar[]
  indicator: ActiveIndicator
  result: IndicatorResult
  isDark: boolean
  /** Controlled total height in px (header + chart). Omit to use the default for this indicator type. */
  height?: number
  onRemove: () => void
  onChartReady: (chart: IChartApi) => void
  onChartDestroy: (chart: IChartApi) => void
  onDataReady?: (chart: IChartApi) => void
}

export function IndicatorPane({
  bars,
  indicator,
  result,
  onDataReady,
  isDark,
  height,
  onRemove,
  onChartReady,
  onChartDestroy,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const lineSeriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map())
  const histSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const anchorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)

  const entry = getIndicator(indicator.id)
  const config = entry?.config

  // Derive heights from prop (controlled) or default for this indicator type
  const totalH = height ?? PANE_H(indicator.id)
  const chartH = Math.max(20, totalH - HEADER_H)

  const bg = isDark ? '#131722' : '#ffffff'
  const textColor = isDark ? '#d1d4dc' : '#131722'
  const gridColor = isDark ? '#1e222d' : '#f0f3fa'
  const borderColor = isDark ? '#2a2e39' : '#e0e3eb'

  // ── Create chart + series (mount only) ──────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || !config) return

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: chartH,
      layout: {
        background: { type: ColorType.Solid, color: bg },
        textColor,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 10,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: gridColor, style: LineStyle.Dotted },
      },
      rightPriceScale: {
        borderColor,
        textColor,
        // Bounded oscillators (RSI etc.) declare priceRange in their config.
        // Small margins ensure axis labels at 0 and 100 are never clipped at
        // the chart boundary; autoscaleInfoProvider locks the data range.
        ...(config.priceRange ? { scaleMargins: { top: 0.08, bottom: 0.05 } } : {}),
      },
      timeScale: {
        visible: false,
        borderColor,
        rightOffset: 20,
        fixRightEdge: false,
        fixLeftEdge: false,
      },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { color: isDark ? '#4c525e' : '#787b86', width: 1, style: LineStyle.Dashed },
      },
      // Sub-pane charts are view-only mirrors — all navigation goes through the
      // main chart which broadcasts its range to sub-panes.
      handleScroll: { mouseWheel: false, pressedMouseMove: false },
      handleScale: { mouseWheel: false, pinch: false, axisPressedMouseMove: false },
    })

    chartRef.current = chart

    // Line series — one per config.lines entry
    const lineMap = new Map<string, ISeriesApi<'Line'>>()
    for (const def of config.lines) {
      const color = indicator.colors?.[def.key] ?? def.color
      const s = chart.addLineSeries({
        color,
        lineWidth: def.width,
        lineStyle:
          def.style === 'dashed'
            ? LineStyle.Dashed
            : def.style === 'dotted'
              ? LineStyle.Dotted
              : LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: true,
      } as DeepPartial<LineSeriesOptions>)
      lineMap.set(def.key, s)
    }
    lineSeriesRef.current = lineMap

    // Hidden anchor series — covers ALL bars (including warmup).
    // autoscaleInfoProvider:null excludes it from price scale calculations
    // so it never fights with the bounded-oscillator scale lock.
    const anchor = chart.addLineSeries({
      visible: false,
      priceLineVisible: false,
      lastValueVisible: false,
      autoscaleInfoProvider: () => null,
    } as DeepPartial<LineSeriesOptions>)
    anchorSeriesRef.current = anchor

    // Histogram series for MACD-type indicators
    if (config.histogramDefs?.length) {
      const def = config.histogramDefs[0]
      const hs = chart.addHistogramSeries({
        priceLineVisible: false,
        lastValueVisible: false,
        color: def?.colorPositive,
      } as DeepPartial<HistogramSeriesOptions>)
      histSeriesRef.current = hs
    }

    // Bounded oscillators: lock price axis to declared range with zero margin.
    // Combined with scaleMargins:0 on rightPriceScale (set above) this prevents
    // LWC from extending the axis beyond the oscillator's valid range.
    if (config.priceRange) {
      const { min, max } = config.priceRange
      for (const s of lineMap.values()) {
        s.applyOptions({
          autoscaleInfoProvider: () => ({
            priceRange: { minValue: min, maxValue: max },
            margins: { above: 0, below: 0 },
          }),
        })
      }
    }

    // Generic reference lines declared in config.priceLevels (e.g. RSI 70/30, MFI 80/20, CMF 0)
    if (config.priceLevels?.length) {
      const firstSeries = Array.from(lineMap.values())[0]
      if (firstSeries) {
        const refColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'
        for (const price of config.priceLevels) {
          firstSeries.createPriceLine({
            price,
            color: refColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: false,
            title: '',
          })
        }
      }
    }

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) chart.applyOptions({ width: e.contentRect.width })
    })
    ro.observe(containerRef.current)

    onChartReady(chart)

    return () => {
      ro.disconnect()
      onChartDestroy(chart)
      chart.remove()
      chartRef.current = null
      lineSeriesRef.current = new Map()
      histSeriesRef.current = null
      anchorSeriesRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // mount only — series structure is fixed per indicator type

  // ── Respond to controlled height changes (vertical resize) ────────────────
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({ height: Math.max(20, chartH) })
    }
  }, [chartH])

  // ── Apply color overrides whenever they change ────────────────────────────
  useEffect(() => {
    if (!config) return
    for (const def of config.lines) {
      const s = lineSeriesRef.current.get(def.key)
      if (!s) continue
      s.applyOptions({ color: indicator.colors?.[def.key] ?? def.color })
    }
  }, [indicator.colors, config])

  // ── Feed data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current || !bars.length) return

    // Keep anchor series in sync with all bars so logical indices match main chart
    anchorSeriesRef.current?.setData(bars.map((b) => ({ time: b.time as UTCTimestamp, value: 0 })))

    const timestamps = bars.map((b) => b.time as UTCTimestamp)

    for (const line of result.lines) {
      const s = lineSeriesRef.current.get(line.key)
      if (!s) continue
      const data = timestamps
        .map((t, i) => {
          const v = line.values[i]
          return v !== null && v !== undefined ? { time: t, value: v } : null
        })
        .filter((d): d is { time: UTCTimestamp; value: number } => d !== null)
      s.setData(data)
    }

    if (result.histograms?.[0] && histSeriesRef.current) {
      const hist = result.histograms[0]
      const data = timestamps
        .map((t, i) => {
          const v = hist.values[i]
          if (v === null || v === undefined) return null
          return {
            time: t,
            value: v,
            color: hist.colors?.[i] ?? (v >= 0 ? '#26a69a' : '#ef5350'),
          }
        })
        .filter((d): d is NonNullable<typeof d> => d !== null)
      histSeriesRef.current.setData(data)
    }

    if (chartRef.current) onDataReady?.(chartRef.current)
  }, [bars, result, onDataReady])

  if (!config) return null

  const paramStr = config.params.map((p) => String(indicator.params[p.key] ?? p.default)).join(', ')
  const label = paramStr ? `${config.shortName} (${paramStr})` : config.shortName

  return (
    <div style={{ height: totalH }} className="relative flex flex-col">
      {/* Header */}
      <div
        style={{ height: HEADER_H }}
        className="flex shrink-0 items-center gap-2 border-t border-tv-border px-2 text-2xs text-tv-text-secondary"
      >
        <span className="font-medium">{label}</span>
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto flex h-4 w-4 items-center justify-center rounded text-tv-text-muted hover:text-tv-text-primary"
          title="Remove indicator"
        >
          ×
        </button>
      </div>

      {/* Chart */}
      <div ref={containerRef} style={{ height: chartH }} className="w-full" />
    </div>
  )
}
